import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Gavel, FileText, Calendar, Building2, AlertTriangle, Award,
  ArrowRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { StarRating } from "@/components/StarRating";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatDateBR, formatDateTimeBR } from "@/lib/api";

const KpiCard = ({ icon: Icon, label, value, hint, accent, to, testId }) => {
  const navigate = useNavigate();
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => to && navigate(to)}
      onKeyDown={(e) => { if (to && (e.key === "Enter" || e.key === " ")) navigate(to); }}
      className="card-soft cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-midnight"
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
            {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
          </div>
          <div className={`p-2 rounded-md ${accent}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function JuridicoDashboard() {
  const [data, setData] = useState(null);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [ranking, setRanking] = useState([]);

  useEffect(() => {
    api.get("/dashboard/").then(({ data }) => setData(data));
    api.get("/documents", { params: { status_filter: "pending_legal" } }).then(({ data }) => setPendingDocs(data));
    api.get("/contracts").then(({ data }) => setContracts(data));
    api.get("/meetings", { params: { upcoming: true } }).then(({ data }) => setMeetings(data));
    api.get("/ratings/ranking", { params: { period: "month" } }).then(({ data }) => setRanking(data.ranking || [])).catch(() => {});
  }, []);

  if (!data) return <Skeleton className="h-96" />;

  // Group contracts by consultant
  const byConsultant = {};
  contracts.forEach(c => {
    if (!c.consultant_id) return;
    if (!byConsultant[c.consultant_id]) byConsultant[c.consultant_id] = { name: c.consultant_name, contracts: [] };
    byConsultant[c.consultant_id].contracts.push(c);
  });

  // Meetings this week
  const now = new Date();
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const meetingsThisWeek = meetings.filter(m => {
    const dt = new Date(m.scheduled_at);
    return dt >= now && dt <= weekFromNow;
  });

  return (
    <div className="space-y-6" data-testid="juridico-dashboard">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Controle Jurídico</h1>
        <p className="text-sm text-slate-600 mt-1">
          Visão consolidada da revisão jurídica, contratos sob controle e desempenho dos consultores. Clique nos cards para navegar.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Gavel}
          label="Fila de Revisões"
          value={data.extras?.queue_pending_legal || 0}
          hint="Aguardando revisão"
          accent="bg-blue-50 text-blue-700"
          to="/juridico/revisoes"
          testId="kpi-juridico-revisoes"
        />
        <KpiCard
          icon={FileText}
          label="Contratos sob Controle"
          value={data.kpis.active_contracts}
          hint="Em execução"
          accent="bg-emerald-50 text-emerald-700"
          to="/juridico/contratos"
          testId="kpi-juridico-contratos"
        />
        <KpiCard
          icon={Calendar}
          label="Reuniões da semana"
          value={meetingsThisWeek.length}
          hint="Próximos 7 dias"
          accent="bg-amber-50 text-amber-700"
          to="/juridico/contratos"
          testId="kpi-juridico-reunioes"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Contratos em Risco"
          value={data.kpis.in_risk}
          hint="<30 dias"
          accent="bg-red-50 text-red-700"
          to="/juridico/contratos?risk=true"
          testId="kpi-juridico-em-risco"
        />
      </div>

      <Tabs defaultValue="queue">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="queue"><Gavel className="w-4 h-4 mr-2" /> Fila de Revisões</TabsTrigger>
          <TabsTrigger value="contracts"><FileText className="w-4 h-4 mr-2" /> Contratos sob Controle</TabsTrigger>
          <TabsTrigger value="meetings"><Calendar className="w-4 h-4 mr-2" /> Reuniões da semana</TabsTrigger>
          <TabsTrigger value="ranking"><Award className="w-4 h-4 mr-2" /> Ranking de Consultores</TabsTrigger>
        </TabsList>

        <TabsContent value="queue">
          <Card className="card-soft">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle className="text-base">Fila de Revisões Jurídicas</CardTitle>
              <Link to="/juridico/revisoes" className="text-xs font-medium text-midnight hover:underline">
                Abrir fila completa
              </Link>
            </CardHeader>
            <CardContent>
              {pendingDocs.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Sem documentos aguardando revisão.</div>
              ) : (
                <div className="space-y-3">
                  {pendingDocs.slice(0, 5).map((d) => (
                    <Link key={d.id} to={`/documentos/${d.id}`}
                      className="block p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                      data-testid={`juridico-doc-${d.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <FileText className="w-4 h-4 text-slate-500" />
                            <span className="font-medium text-slate-900">{d.title}</span>
                            <StatusBadge status={d.status} />
                            <span className="text-xs text-slate-500">v{d.current_version}</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            Enviado por {d.created_by_name} em {formatDateTimeBR(d.updated_at)}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 mt-1" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contracts">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base">Contratos por consultor</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(byConsultant).length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Sem contratos atribuídos.</div>
              ) : (
                <div className="space-y-5">
                  {Object.entries(byConsultant).map(([cid, group]) => (
                    <div key={cid}>
                      <div className="text-sm font-semibold text-slate-900 mb-2">{group.name}</div>
                      <div className="space-y-2">
                        {group.contracts.map(c => (
                          <Link key={c.id} to={`/juridico/contratos/${c.id}`}
                            className="block p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div>
                                <div className="text-sm font-medium text-slate-900">
                                  <Building2 className="inline w-4 h-4 text-slate-500 mr-1" />
                                  {c.client_name} · {c.product_name}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                  Vence em {formatDateBR(c.end_date)} ·
                                  <span className={c.days_remaining < 30 ? "text-red-600 font-medium ml-1" : "ml-1"}>
                                    {c.days_remaining} dias
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <StatusBadge status={c.status} />
                                <StatusBadge status={c.days_remaining < 30 ? "risk" : c.days_remaining < 60 ? "attention" : "on_track"} />
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-3">
                              <Progress value={c.progress_pct} className="h-1.5 flex-1" />
                              <span className="text-xs text-slate-600 w-12 text-right">{c.progress_pct}%</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meetings">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base">Reuniões da semana</CardTitle>
            </CardHeader>
            <CardContent>
              {meetingsThisWeek.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Sem reuniões na semana.</div>
              ) : (
                <div className="space-y-2">
                  {meetingsThisWeek.map(m => (
                    <Link to={`/secretaria/reunioes/${m.id}`} key={m.id} className="block p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <div className="text-sm font-medium text-slate-900">{m.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {formatDateTimeBR(m.scheduled_at)} · {m.duration_minutes} min · <StatusBadge status={m.status} />
                        {m.consultant_name && <span className="ml-2">· Consultor: <strong>{m.consultant_name}</strong></span>}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ranking">
          <Card className="card-soft">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-4 h-4 text-gold-deep" /> Top consultores do mês
              </CardTitle>
              <Link to="/juridico/ranking" className="text-xs font-medium text-midnight hover:underline">
                Ver ranking completo
              </Link>
            </CardHeader>
            <CardContent>
              {ranking.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Sem avaliações no mês ainda.</div>
              ) : (
                <ol className="space-y-2">
                  {ranking.slice(0, 5).map((r, i) => (
                    <li key={r.consultant_id} className="p-3 border border-slate-200 rounded-md flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-midnight text-white flex items-center justify-center text-xs font-semibold">
                          {i + 1}
                        </span>
                        <div>
                          <div className="text-sm font-medium">{r.consultant_name}</div>
                          <div className="text-xs text-slate-500">{r.total_ratings} avaliações</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StarRating value={Math.round(r.avg)} readOnly size={14} testIdPrefix={`top-${i}`} />
                        <span className="text-sm font-semibold text-gold-deep">{r.avg.toFixed(2)}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
